import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Box, Dialog, DialogContent, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setSnackbar } from '../../redux/messageSlice';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import '@fontsource/rubik';

import AuthLayout from '../layouts/AuthLayout';
import useCustomTheme from '../../theme/useCustomTheme';

import useCustomerDashboard from './hooks/useCustomerDashboard';
import CustomerDashboardNavbar from './components/CustomerDashboardNavbar';
import DashboardHeader from './components/DashboardHeader';
import UsageMetricsRow from './components/UsageMetricsRow';
import ProjectStatusCards from './components/ProjectStatusCards';
import MonthlyOutputChart from './components/MonthlyOutputChart';
import TeamBreakdownCard from './components/TeamBreakdownCard';
import ContractSidebar from './components/ContractSidebar';
import VideoTypesDonut from './components/VideoTypesDonut';
import QuickTurnRulesCard from './components/QuickTurnRulesCard';
import ExportButton from './components/ExportButton';
import SupportFAB from './components/SupportFAB';
import ProductTour from '../../components/producttour/ProductTour';
import { CUSTOMER_DASHBOARD_TOUR_STEPS } from './tourSteps';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';

const CustomerDashboard = (props) => {
  const [theme] = useCustomTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [chartFilter, setChartFilter] = useState({
    fromMonth: '', fromYear: '', toMonth: '', toYear: '',
  });
  const userHasChangedFilter = useRef(false);

  const filterParams = useMemo(() => {
    if (!userHasChangedFilter.current) return {};
    const fromComplete = chartFilter.fromMonth && chartFilter.fromYear;
    const toComplete = chartFilter.toMonth && chartFilter.toYear;
    if (!fromComplete || !toComplete) return {};
    const y = parseInt(chartFilter.toYear, 10);
    const m = parseInt(chartFilter.toMonth, 10);
    const lastDay = new Date(y, m, 0).getDate();
    return {
      from: `${chartFilter.fromYear}-${chartFilter.fromMonth}-01`,
      to: `${chartFilter.toYear}-${chartFilter.toMonth}-${String(lastDay).padStart(2, '0')}`,
    };
  }, [chartFilter]);

  const { dashboardData, isLoading, isError, httpStatus, apiMessage } = useCustomerDashboard(filterParams);

  // 422 = date range validation error — show inline, not as a blocking popup
  const isValidationError = isError && httpStatus === 422;
  const isNetworkError = isError && !isValidationError;

  const [showTimeoutPopup, setShowTimeoutPopup] = useState(false);
  const timeoutRef = useRef(null);

  const [tourOpen, setTourOpen] = useState(
    () => window.localStorage.getItem('cd_tour_seen') !== 'true'
  );
  const tourButtonPos = { right: 100, bottom: 28 };

  useEffect(() => {
    if (isLoading) {
      setShowTimeoutPopup(false);
      timeoutRef.current = setTimeout(() => setShowTimeoutPopup(true), 10000);
    } else {
      clearTimeout(timeoutRef.current);
      if (!isNetworkError) setShowTimeoutPopup(false);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [isLoading, isNetworkError]);

  const showErrorPopup = isNetworkError || showTimeoutPopup;

  useEffect(() => {
    if (isValidationError) {
      dispatch(setSnackbar({
        type: 'error',
        flag: true,
        message: apiMessage || 'Date range cannot exceed 36 months. Please select a shorter range.',
      }));
    }
  }, [isValidationError, apiMessage]);

  // Seed dropdowns from API's default filter once data arrives
  const apiFilter = dashboardData?.filter;
  useEffect(() => {
    if (!apiFilter || userHasChangedFilter.current) return;
    const parseDate = (dateStr) => {
      if (!dateStr) return { month: '', year: '' };
      const parts = dateStr.split('-');
      return { month: parts[1] || '', year: parts[0] || '' };
    };
    const from = parseDate(apiFilter.from);
    const to = parseDate(apiFilter.to);
    setChartFilter({
      fromMonth: from.month, fromYear: from.year,
      toMonth: to.month, toYear: to.year,
    });
  }, [apiFilter]);

  const handleChartFilterChange = useCallback((next) => {
    userHasChangedFilter.current = true;
    setChartFilter(next);
  }, []);

  const summary       = dashboardData?.summary;
  const projectStatus = dashboardData?.projectStatus;
  const monthlyOutput = dashboardData?.monthlyOutput;
  const teamBreakdown = dashboardData?.teamBreakdown;
  const videoTypes    = dashboardData?.videoTypes;
  const supportEmail  = summary?.support_email || 'support@lucihub.com';

  return (
    <AuthLayout uploadProps={props.uploadProps}>
      {/* ── Sticky top navbar matching HTML topbar design ── */}
      <CustomerDashboardNavbar />

      {/* ── Scrollable content (captured for PDF export) ── */}
      <Box
        id="customer-dashboard-content"
        sx={{
          px: { xs: 3, md: '40px', lg: '56px' },
          pt: { xs: 3, md: '26px' },
          pb: { xs: 8, md: '60px' },
          fontFamily: 'Rubik',
        }}
      >
        {/* ── Page Header + Export button inline ── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 0 }}>
          <Box id="cd-header" sx={{ minWidth: 0 }}>
            <DashboardHeader summary={summary} isLoading={isLoading} />
          </Box>
          <Box id="cd-export-button" sx={{ pt: 0.75, flexShrink: 0 }}>
            <ExportButton dashboardData={dashboardData} isLoading={isLoading} />
          </Box>
        </Box>

        {/* ── Two-column layout ── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 280px', xl: 'minmax(0, 1fr) 300px' },
            gap: { xs: 2, lg: 2.5 },
            alignItems: 'start',
          }}
        >
          {/* ── MAIN COLUMN ── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, lg: 2.5 }, minWidth: 0 }}>
            <Box id="cd-usage-metrics">
              <UsageMetricsRow summary={summary} isLoading={isLoading} />
            </Box>
            <Box id="cd-project-status">
              <ProjectStatusCards projectStatus={projectStatus} isLoading={isLoading} />
            </Box>
            <MonthlyOutputChart
              monthlyOutput={monthlyOutput}
              projectStatus={projectStatus}
              isLoading={isLoading}
              filter={{ ...chartFilter, from: filterParams.from, to: filterParams.to }}
              onFilterChange={handleChartFilterChange}
            />
            <TeamBreakdownCard teamBreakdown={teamBreakdown} isLoading={isLoading} />
          </Box>

          {/* ── SIDEBAR ── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, lg: 2.5 } }}>
            <Box id="cd-contract-sidebar">
              <ContractSidebar summary={summary} isLoading={isLoading} />
            </Box>
            <VideoTypesDonut videoTypes={videoTypes} isLoading={isLoading} />
            <Box id="cd-quick-turn-rules">
              <QuickTurnRulesCard />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Floating support button ── */}
      <SupportFAB supportEmail={supportEmail} />

      {/* ── Floating "Take a Tour" button ── */}
      <Box
        component="button"
        onClick={() => setTourOpen(true)}
        aria-label="Take a tour of the Account Dashboard"
        sx={{
          position: 'fixed',
          bottom: tourButtonPos.bottom,
          right: tourButtonPos.right,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          height: 48,
          px: '18px',
          border: 0,
          borderRadius: '48px',
          backgroundColor: '#08231B',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(8,35,27,0.28)',
          transition: 'right 0.2s ease, bottom 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(8,35,27,0.40), 0 2px 8px rgba(8,35,27,0.2)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <MapOutlinedIcon sx={{ fontSize: 18, color: '#00D744' }} />
        <Typography
          sx={{
            fontFamily: 'Rubik',
            fontSize: '12.5px',
            fontWeight: 600,
            color: '#f4f4f1',
            whiteSpace: 'nowrap',
          }}
        >
          Take a Tour
        </Typography>
      </Box>

      {/* ── Guided product tour ── */}
      <ProductTour
        open={tourOpen}
        onClose={() => {
          window.localStorage.setItem('cd_tour_seen', 'true');
          setTourOpen(false);
        }}
        steps={CUSTOMER_DASHBOARD_TOUR_STEPS}
      />

      {/* ── Error / timeout popup ── */}
      <Dialog
        open={showErrorPopup}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: '12px',
            maxWidth: 400,
            textAlign: 'center',
          },
        }}
      >
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
          <ErrorOutlineIcon sx={{ fontSize: 52, color: '#e53935' }} />
          <Typography sx={{ fontFamily: 'Rubik', fontSize: '18px', fontWeight: 700, color: '#08231B' }}>
            {showTimeoutPopup ? 'Connection timed out' : 'Network error'}
          </Typography>
          <Typography sx={{ fontFamily: 'Rubik', fontSize: '13px', color: '#6b7470', lineHeight: 1.6 }}>
            {showTimeoutPopup
              ? 'The request is taking longer than expected. Please check your internet connection and try again.'
              : 'Unable to reach the server. Please check your network connection and try again.'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{
              fontFamily: 'Rubik',
              fontWeight: 600,
              fontSize: '13px !important',
              textTransform: 'none',
              backgroundColor: '#08231B',
              color: '#ffffff',
              borderRadius: '10px',
              px: 4,
              py: 1,
              mt: 1,
              '&:hover': { backgroundColor: '#0d3528', color: '#ffffff' },
            }}
          >
            Go to Main Dashboard
          </Button>
        </DialogContent>
      </Dialog>
    </AuthLayout>
  );
};

export default CustomerDashboard;
